import React, {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type QAWidgetProps,
  type QAThemingProps,
  QA_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionQA for imperative control.
 */
export interface LegionQAHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildQAWidgetUrl(props: QAWidgetProps): string {
  const {
    baseUrl,
    productId,
    username = "Anonymous",
    pageSize = 5,
    theme = "light",
    showSearch = true,
    showAskButton = true,
  } = props;

  const url = new URL("/widget/qa", baseUrl);

  if (productId) {
    url.searchParams.set("productId", productId);
  }
  if (username) {
    url.searchParams.set("username", username);
  }
  if (pageSize) {
    url.searchParams.set("pageSize", String(pageSize));
  }
  if (theme) {
    url.searchParams.set("theme", theme);
  }
  url.searchParams.set("showSearch", String(showSearch));
  url.searchParams.set("showAskButton", String(showAskButton));

  // Append theming params
  const themingKeys = Object.keys(QA_THEMING_PROP_TO_PARAM) as Array<
    keyof QAThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(QA_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionQA – a React component that embeds the Legion Q&A widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionQA } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionQA
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionQA = forwardRef<LegionQAHandle, QAWidgetProps>(
  function LegionQA(props, ref) {
    const {
      theme = "light",
      authToken,
      productId,
      username,
      pageSize,
      showSearch,
      showAskButton,
      width = "600px",
      height = "800px",
      className,
      style,
      onLoad,
      onError,
      baseUrl,
      ...rest
    } = props;

    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.location.reload();
          }
        },
      }),
      []
    );

    const src = useMemo(
      () => buildQAWidgetUrl(props),
      [
        baseUrl,
        productId,
        username,
        pageSize,
        theme,
        showSearch,
        showAskButton,
        ...Object.keys(QA_THEMING_PROP_TO_PARAM).map(
          (k) => rest[k as keyof typeof rest]
        ),
      ]
    );

    const handleLoad = useCallback(() => {
      // Send auth token via postMessage
      if (authToken && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token: authToken },
          baseUrl
        );
      }
      onLoad?.({ widget: "qa", theme });
    }, [authToken, baseUrl, theme, onLoad]);

    const handleError = useCallback(() => {
      onError?.("Failed to load Q&A widget");
    }, [onError]);

    const containerStyle: React.CSSProperties = {
      display: "block",
      width,
      height,
      ...style,
    };

    const iframeStyle: React.CSSProperties = {
      border: "none",
      borderRadius: "8px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      width: "100%",
      height: "100%",
    };

    return (
      <div className={className} style={containerStyle}>
        <iframe
          ref={iframeRef}
          src={src}
          title="Community Q&A Widget"
          width="100%"
          height="100%"
          loading="lazy"
          style={iframeStyle}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }
);

